import { AxiosInstance } from "axios";
import {
  AccountCompanyDetails,
  EnterpriseAccountDetails,
  failure,
  isOk,
  ok,
  pending,
  ProductName,
  productNames,
  Result,
} from "../common/common";
import { makeAutoObservable } from "mobx";
import { BooleanFieldModel, RadioFieldModel, StringFieldModel } from "../common/commonForms";
import { isBlank, removeAllWhitespace } from "../utils/strings";
import { TMessageKey } from "../i18n/types.gen";
import { TabProps } from "../components-v2/Tabs";
import { newUuid } from "../utils/uuid";
import { TMessage } from "../i18n/tt";
import AlertMessageStore from "../stores/AlertMessageStore";
import { Language } from "../i18n/languages";
import { formatNumber } from "../utils/numbers";
import { EventDispatcher, navigate, showAlert } from "../events/eventsDomain";
import { superadminUrl } from "../utils/navigation";
import { SharepointSettings } from "../sharepoint/sharepointDomain";
import { UserByEmailOrSocialSecurityNumber } from "../AdminApp/AdminAppController";

export class EnterpriseAccountsStore {
  accountResult?: Result<EnterpriseAccountDetails>;
  nameFilter: StringFieldModel = new StringFieldModel();
  subscriptionTypeFilter?: RadioFieldModel<ProductName | null>;
  addCompaniesModel?: AddCompaniesModel;
  submitCompaniesResult?: Result;
  sharepointSettingsResult?: Result<SharepointSettings | undefined>;
  activeTab: EnterpriseAccountsTab = "companies";
  integrationSettingsModel?: IntegrationSettingsModel;
  submitSharepointSettingsResult?: Result<TMessage, TMessage>;
  showDialog?: EnterpriseAccountDialog;

  constructor(
    readonly http: AxiosInstance,
    readonly messageStore: AlertMessageStore,
    readonly dispatch: EventDispatcher,
    readonly id: string,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
  }

  *fetchEnterpriseAccount() {
    this.accountResult = pending();
    try {
      const response: EnterpriseAccountDetails = yield this.http
        .get(`/enterprise-accounts/${this.id}`)
        .then((response) => response.data);
      this.accountResult = ok(response);

      this.subscriptionTypeFilter = new RadioFieldModel<ProductName | null>({
        value: null,
        options: [null, ...response.productNames],
        idFn: (s) => (s ? s : "all"),
        labelFn: (s) => (s ? productNameLabel(s) : { key: "all" }),
      });
    } catch (e) {
      this.accountResult = failure();
    }
  }

  get companyData() {
    return isOk(this.accountResult) ? this.filter(this.accountResult.data) : [];
  }

  private filter(data: EnterpriseAccountDetails) {
    const searchString = this.nameFilter.inputValue.toLowerCase();
    const details = searchString
      ? data.companyDetails.filter((c) => c.companyName.toLowerCase().includes(searchString))
      : data.companyDetails;

    const subscriptionType = this.subscriptionTypeFilter?.value;
    return subscriptionType ? details.filter((d) => d.productName === subscriptionType) : details;
  }

  showAddCompaniesDialog() {
    this.addCompaniesModel = new AddCompaniesModel(this.http, this.id, this.fetchEnterpriseAccount);
  }

  hideAddCompaniesDialog() {
    this.addCompaniesModel = undefined;
  }

  *submitAddCompaniesDialog() {
    if (!this.addCompaniesModel || !this.addCompaniesModel?.validate()) return;

    const orgNos = this.addCompaniesModel.orgNos.inputValue
      .split(/[,\n]+/)
      .map((s) => removeAllWhitespace(s))
      .filter((s) => !isBlank(s));

    this.submitCompaniesResult = pending();
    try {
      const response: AddCompaniesToEnterpriseAccountResponse = yield this.http
        .post(`/enterprise-accounts/${this.id}/company`, {
          orgNos,
          subscriptionType: this.addCompaniesModel.subscriptionModel.value,
          completeOnboarding: this.addCompaniesModel.completeOnboarding.value,
        })
        .then((r) => r.data);
      this.submitCompaniesResult = ok();

      this.dispatch([
        showAlert(
          {
            key:
              response.notFound.length > 0
                ? "enterprise-account.added-companies-missing"
                : "enterprise-account.added-companies",
            params: {
              count: response.numberOfAddedCompanies,
              orgNos: response.notFound.join(", "),
            },
          },
          "info",
        ),
      ]);

      this.addCompaniesModel = undefined;
      this.fetchEnterpriseAccount();
    } catch (e) {
      this.submitCompaniesResult = failure();
    }
  }

  setActiveTab(tab: EnterpriseAccountsTab) {
    this.activeTab = tab;
  }

  *fetchSharepointSettings() {
    this.sharepointSettingsResult = pending();
    try {
      const settings: SharepointSettings | undefined = yield this.http
        .get(`/enterprise-accounts/${this.id}/sharepoint-settings`)
        .then((response) => {
          if (response.data.id) {
            return response.data;
          } else {
            return undefined;
          }
        });
      this.sharepointSettingsResult = ok(settings);
    } catch (e) {
      this.sharepointSettingsResult = failure();
    }
  }

  get tabs(): TabProps[] {
    const tabs: EnterpriseAccountsTab[] = ["companies", "account", "integrations"];
    return tabs.map<TabProps>((tab) => ({
      id: tab,
      isActive: this.activeTab === tab,
      label: { key: enterpriseAccountsTabLabels[tab], params: { count: this.companyData.length } },
      onSelect: () => {
        this.setActiveTab(tab);
        if (tab === "integrations") {
          this.fetchSharepointSettings();
        }
      },
    }));
  }

  toggleSharepointSettingsDialog() {
    if (this.integrationSettingsModel) {
      this.integrationSettingsModel = undefined;
    } else {
      if (isOk(this.sharepointSettingsResult))
        this.integrationSettingsModel = createSharepointSettingsModel(
          this.sharepointSettingsResult.data,
        );
    }
  }

  *addOrUpdateSharepointSettings() {
    if (!this.integrationSettingsModel || !this.integrationSettingsModel?.validate()) return;
    this.submitSharepointSettingsResult = pending();
    const data: SharepointSettings = {
      id: newUuid(), // backend will figure out the correct id based on the existence of sharepoint settings on the account
      siteId: this.integrationSettingsModel.siteID.validatedValue,
      applicationId: this.integrationSettingsModel.clientID.validatedValue,
      secret: this.integrationSettingsModel.clientSecret.validatedValue,
      tenantId: this.integrationSettingsModel.tenantID.validatedValue,
    };
    try {
      this.sharepointSettingsResult = yield this.http.post(
        `/enterprise-accounts/${this.id}/sharepoint-settings`,
        data,
      );
      this.submitSharepointSettingsResult = ok({ key: "general-success" });
      this.fetchSharepointSettings();
      this.toggleSharepointSettingsDialog();
    } catch (e) {
      this.submitSharepointSettingsResult = failure({ key: "sharepoint.connection-failed" });
    }
  }

  *removeFromAccount(orgNo: string) {
    try {
      yield this.http.delete(`/enterprise-accounts/${this.id}/company/${orgNo}`);

      this.fetchEnterpriseAccount();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  openRemoveAllCompaniesDialog() {
    this.showDialog = "remove-all-companies";
  }

  closeDialog() {
    this.showDialog = undefined;
  }

  *removeAllCompanies() {
    try {
      yield this.http.delete(`/enterprise-accounts/${this.id}/company`);

      this.closeDialog();
      this.fetchEnterpriseAccount();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  openDeleteAccountDialog() {
    this.showDialog = "delete-account";
  }

  *deleteAccount() {
    try {
      yield this.http.delete(`/enterprise-accounts/${this.id}`);

      this.closeDialog();
      this.dispatch([navigate(`${superadminUrl}/enterprise-accounts`)]);
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  *updateSubscription(row: AccountCompanyDetails, productName: ProductName | null) {
    if (row.productName === productName) return;

    try {
      yield this.http.post(`/enterprise-accounts/${this.id}/company/${row.orgNo}/subscription`, {
        productName,
      });

      this.messageStore.showSuccess({ key: "enterprise-account.update-subscription-success" });
      this.fetchEnterpriseAccount();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }
}

export class AddCompaniesModel {
  readonly http: AxiosInstance;
  readonly id: string;
  readonly onSuccess: () => void;
  readonly orgNos = new StringFieldModel({ required: true });
  readonly searchField = new StringFieldModel();
  readonly subscriptionModel = new RadioFieldModel<ProductName | null>({
    value: null,
    options: [null, ...productNames],
    labelFn: productNameLabel,
    idFn: (s) => (s ? s : "none"),
  });
  readonly completeOnboarding = new BooleanFieldModel();
  searchResult?: Result<UserByEmailOrSocialSecurityNumber[]>;
  fetchResult?: Result;

  constructor(http: AxiosInstance, id: string, onSuccess: () => void) {
    this.http = http;
    this.id = id;
    this.onSuccess = onSuccess;

    makeAutoObservable(this, { http: false }, { autoBind: true });
  }

  validate() {
    return this.orgNos.validate();
  }

  *search() {
    if (!this.searchField.inputValue) return;

    this.searchResult = pending();
    try {
      const response: UserByEmailOrSocialSecurityNumber[] = yield this.http
        .get(`/superadmin/lookup-user/${this.searchField.inputValue}`)
        .then((response) => response.data);
      this.searchResult = ok(response);
    } catch (e) {
      this.searchResult = failure();
    }
  }

  *fetchUserCompanies(userId: string) {
    this.fetchResult = pending();
    try {
      const orgNos: string[] = yield this.http
        .get(`/superadmin/user/${userId}/admin-access`)
        .then((response) => response.data);

      this.orgNos.inputValue = orgNos.join("\n");
      this.fetchResult = ok();
    } catch (e) {
      this.fetchResult = failure();
    }
  }
}

const createSharepointSettingsModel = (settings?: SharepointSettings): SharepointSettingsModel => {
  const siteID = new StringFieldModel({ value: settings?.siteId, required: true });
  const clientID = new StringFieldModel({ value: settings?.applicationId, required: true });
  const clientSecret = new StringFieldModel({ value: settings?.secret, required: true });
  const tenantID = new StringFieldModel({ value: settings?.tenantId, required: true });
  const validate = (): boolean => {
    const results = [siteID, clientID, clientSecret, tenantID].map((validator) =>
      validator.validate(),
    );
    return results.every(Boolean);
  };

  return {
    type: "sharepoint",
    siteID,
    clientID,
    clientSecret,
    tenantID,
    validate,
  };
};

type EnterpriseAccountsTab = "companies" | "account" | "integrations";
const enterpriseAccountsTabLabels: { [key in EnterpriseAccountsTab]: TMessageKey } = {
  companies: "enterprise-account.companies",
  account: "enterprise-account.account",
  integrations: "enterprise-account.integrations",
};

export type IntegrationSettingsModel = SharepointSettingsModel;

export type SharepointSettingsModel = {
  type: "sharepoint";
  siteID: StringFieldModel;
  clientID: StringFieldModel;
  clientSecret: StringFieldModel;
  tenantID: StringFieldModel;
  validate: () => boolean;
};

export function formatAccountStatus(
  lang: Language,
  data: EnterpriseAccountDetails,
  productName: ProductName,
) {
  const subscriptionCount = data.subscriptionStats.find(
    (s) => s.productName === productName,
  )?.subscriptionCount;
  const subscriptionLimit = data.account.subscriptionLimits[productName];
  return (
    formatNumber(lang, subscriptionCount ?? 0) + " / " + formatNumber(lang, subscriptionLimit ?? 0)
  );
}

export type EnterpriseAccountDialog = "remove-all-companies" | "delete-account";

export function productNameLabel(productName: ProductName | null): TMessage {
  switch (productName) {
    case "shareholder_register":
      return { key: "enterprise-account.shareholder-register" };
    case "digital_company":
      return { key: "enterprise-account.digital-company" };
    case "enterprise":
      return { translated: "Enterprise" };
    case "pro":
      return { translated: "Pro" };
    case "basic":
      return { translated: "Basic" };
    case null:
      return { key: "enterprise-account.no-subscription" };
  }
}

export function formatLink(link: string) {
  return link.startsWith("https://") ? link : "https://" + link;
}

type AddCompaniesToEnterpriseAccountResponse = {
  numberOfAddedCompanies: number;
  notFound: string[];
};
